import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, CardHeader,
  Badge, Spinner, Table, Input, Label,
} from "reactstrap";
import { Link } from "react-router-dom";
import requests from "../requests";
import { EarningsEntry, FeaturedTeaser, PatternTeaser, daysUntil } from "../hooks/useEarnings";
import { useFundamentals, formatMarketCap } from "../hooks/useFundamentals";
import { usePostHog } from "@posthog/react";
import { buildViewFeaturedUrl, buildPatternDetailUrl, buildDashboardUrl } from "../utils/links";
import { useSeo } from "../hooks/useSeo";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// featured/pattern window (trading days) → human label.
const NUM_TO_LABEL: Record<number, string> = {
  10: "2w", 20: "4w", 65: "3m", 130: "6m", 195: "9m", 260: "1y", 22: "1m", 45: "2m",
};

// Momentum is shown only when notable (top/bottom band) so it reads as a signal, not noise.
const MOM_HIGH = 80;
const MOM_LOW = 20;

const prettyPattern = (t: string) => t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

function formatDateLabel(dateStr: string): string {
  const d = daysUntil(dateStr);
  const date = new Date(dateStr + "T00:00:00");
  const dow = DAY_LABELS[date.getDay()];
  const mon = date.toLocaleString("default", { month: "short" });
  const day = date.getDate();
  if (d === 0) return `Today · ${dow} ${mon} ${day}`;
  if (d === 1) return `Tomorrow · ${dow} ${mon} ${day}`;
  return `${dow} ${mon} ${day} · ${d}d away`;
}

function urgencyColor(dateStr: string): string {
  const d = daysUntil(dateStr);
  if (d <= 1) return "danger";
  if (d <= 3) return "warning";
  return "secondary";
}

function whenLabel(dateStr: string): string {
  const d = daysUntil(dateStr);
  if (d <= 0) return "today";
  if (d === 1) return "tomorrow";
  return `in ${d} days`;
}

// The "active signal" set for the filter + ordering = the headline DejaChart signals
// (featured analog match or classic pattern). Momentum is context, not the headline.
const hasSignal = (e: EarningsEntry): boolean =>
  !!(e.signals?.featured || e.signals?.pattern);

// Sort within a day: bullish featured first, then bearish, then unsignaled — alpha within each.
function sortEntries(entries: EarningsEntry[]): EarningsEntry[] {
  const rank = (e: EarningsEntry) => {
    const f = e.signals?.featured;
    if (!f) return 2;
    return f.direction === "up" ? 0 : 1;
  };
  return [...entries].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    return a.ticker.localeCompare(b.ticker);
  });
}

// Featured tickers go to ViewFeatured (framed in the right direction with the analog
// overlay) — the raw matcher is direction-agnostic, so a 67%-down bearish signal reads
// as "33% win rate" there. Unsignaled tickers go to the matcher at the default window.
function tickerLink(ticker: string, featured?: FeaturedTeaser | null, pattern?: PatternTeaser | null): string {
  // Featured (the analog signal) is the headline — prioritized when a ticker has both.
  // The teaser's `unlock` token lets a logged-out click open the signal in full (like a
  // share link), so the calendar's signals are truly "free to view for everyone".
  if (featured) {
    return buildViewFeaturedUrl({ ticker, direction: featured.direction, window: featured.window, unlock: featured.unlock });
  }
  // Classic-pattern-only ticker → the pattern detail page. lookback_days=7 matches the
  // teaser's recency window (the pattern may have been detected up to 7 days ago, not
  // only on the latest detected date — PatternDetail defaults to 1). The `unlock` token
  // opens the pattern for logged-out viewers (window is always in the teaser now).
  if (pattern?.start_date) {
    return buildPatternDetailUrl({ ticker, patternType: pattern.pattern_type, startDate: pattern.start_date, window: pattern.window, lookbackDays: 7, unlock: pattern.unlock });
  }
  return buildDashboardUrl({ ticker });
}

function sentimentColor(sentiment: string): string {
  if (sentiment === "bullish") return "var(--bs-success)";
  if (sentiment === "bearish") return "var(--bs-danger)";
  return "var(--bs-secondary)";
}

export default function EarningsPage() {
  // This page had no useSeo at all, so it inherited index.html's homepage title and
  // description — one more duplicate-title URL in Search Console, ranking for nothing.
  // The title targets the query someone actually types ("earnings this week"), and the
  // description leads with what only this page has: the chart-analog signal on each
  // reporting name.
  useSeo({
    title: "Earnings This Week — Stocks Reporting in the Next 7 Days",
    description:
      "Every stock reporting earnings in the next 7 days, with the chart signal on each one: what happened historically to stocks whose price pattern looked like this, plus momentum rank and any classic chart pattern forming now.",
    path: "/earnings",
  });
  const getFundamentals = useFundamentals();
  const posthog = usePostHog();
  const [grouped, setGrouped] = useState<Record<string, EarningsEntry[]>>({});
  const [loading, setLoading] = useState(true);
  const [onlySignals, setOnlySignals] = useState(false);

  useEffect(() => {
    requests.get("/api/earnings").then(resp => {
      const entries: EarningsEntry[] = resp.data.earnings ?? [];
      const map: Record<string, EarningsEntry[]> = {};
      for (const e of entries) {
        const d = daysUntil(e.date);
        if (d < 0 || d > 7) continue;
        if (!(e.date in map)) map[e.date] = [];
        map[e.date].push(e);
      }
      for (const date of Object.keys(map)) {
        map[date] = sortEntries(map[date]);
      }
      setGrouped(map);
      setLoading(false);
      const total = entries.filter(e => { const d = daysUntil(e.date); return d >= 0 && d <= 7; }).length;
      const withSignals = entries.filter(e => { const d = daysUntil(e.date); return d >= 0 && d <= 7 && (e.signals?.featured || e.signals?.pattern); }).length;
      posthog.capture("earnings_page_viewed", { total_companies: total, with_signals: withSignals });
    }).catch((err) => {
      // Log internally only — the UI shows a generic empty state, never the reason.
      // eslint-disable-next-line no-console
      console.error("Failed to load /api/earnings:", err);
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply the "only active signals" filter at render time (no refetch).
  const visible: Record<string, EarningsEntry[]> = {};
  for (const date of Object.keys(grouped)) {
    const rows = onlySignals ? grouped[date].filter(hasSignal) : grouped[date];
    if (rows.length) visible[date] = rows;
  }
  const dates = Object.keys(visible).sort();
  const totalCompanies = Object.keys(grouped).reduce((n, d) => n + grouped[d].length, 0);
  const signalCount = Object.keys(grouped).reduce((n, d) => n + grouped[d].filter(hasSignal).length, 0);

  return (
    <Container className="py-4">
      {/* h1, not h2: this page had no h1 at all, so nothing declared its topic. */}
      <h1 className="mb-1 h2" style={{ fontWeight: 700 }}>Earnings This Week</h1>
      <p className="text-muted mb-3" style={{ fontSize: 15 }}>
        Companies reporting earnings in the next 7 days
        {!loading && totalCompanies > 0 && ` · ${totalCompanies} companies`}
        {!loading && signalCount > 0 && (
          <> · <span className="text-success fw-semibold">{signalCount} with active signals</span></>
        )}
      </p>

      {!loading && signalCount > 0 && (
        <div className="mb-4 form-check form-switch">
          <Input
            type="checkbox"
            role="switch"
            id="onlySignals"
            checked={onlySignals}
            onChange={e => setOnlySignals(e.target.checked)}
          />
          <Label for="onlySignals" check style={{ fontSize: 14 }}>
            Only show companies with an active signal
          </Label>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : dates.length === 0 ? (
        <Card><CardBody><p className="text-muted mb-0 text-center py-3">
          {onlySignals
            ? "No companies with an active signal report in the next 7 days."
            : "No upcoming earnings to show right now. Please check back soon."}
        </p></CardBody></Card>
      ) : (
        <Row className="g-4">
          {dates.map(date => (
            <Col xs={12} lg={6} key={date}>
              <Card className="h-100">
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <span style={{ fontWeight: 600 }}>{formatDateLabel(date)}</span>
                  <Badge color={urgencyColor(date)} pill>
                    {visible[date].length}
                  </Badge>
                </CardHeader>
                <CardBody className="p-0">
                  <Table hover className="mb-0" style={{ fontSize: 13, tableLayout: "fixed", width: "100%" }}>
                    <colgroup>
                      <col style={{ width: "24%" }} />
                      <col style={{ width: "40%" }} />
                      <col style={{ width: "16%" }} />
                      <col style={{ width: "20%" }} />
                    </colgroup>
                    <thead>
                      <tr className="text-muted" style={{ whiteSpace: "nowrap" }}>
                        <th className="ps-3">Ticker</th>
                        <th>Company</th>
                        <th>Mkt Cap</th>
                        <th className="text-end pe-3">EPS Est.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible[date].map(e => {
                        const featured = e.signals?.featured ?? null;
                        const pattern = e.signals?.pattern ?? null;
                        const mom = e.signals?.momentum_rank ?? null;
                        const momNotable = mom && (mom.upr_rank >= MOM_HIGH || mom.upr_rank <= MOM_LOW);
                        const f = getFundamentals(e.ticker);
                        const borderColor = featured
                          ? featured.direction === "up" ? "var(--bs-success)" : "var(--bs-danger)"
                          : "transparent";
                        return (
                          <tr key={e.ticker} style={{ borderLeft: `3px solid ${borderColor}` }}>
                            <td className="ps-3" style={{ verticalAlign: "top" }}>
                              <Link
                                to={tickerLink(e.ticker, featured, pattern)}
                                style={{ fontWeight: 600, color: "var(--primary)" }}
                                onClick={() => posthog.capture("earnings_ticker_clicked", {
                                  ticker: e.ticker,
                                  has_featured: !!featured,
                                  has_pattern: !!pattern,
                                  days_until: daysUntil(e.date),
                                })}
                              >
                                {e.ticker}
                              </Link>
                              {featured && (
                                <span
                                  title={`Earnings ${whenLabel(date)} — event risk: the analog forecast doesn't price the earnings catalyst.`}
                                  style={{
                                    display: "block", fontSize: 10, fontWeight: 500, lineHeight: 1.3,
                                    marginTop: 3,
                                    color: featured.direction === "up" ? "var(--bs-success)" : "var(--bs-danger)",
                                  }}
                                >
                                  {featured.direction === "up" ? "▲" : "▼"} {NUM_TO_LABEL[featured.window] ?? featured.window} <span title="Earnings event risk">⚠</span>
                                </span>
                              )}
                              {pattern && (
                                <span
                                  title={pattern.sentiment
                                    ? `${prettyPattern(pattern.pattern_type)} · ${pattern.sentiment} · confidence ${pattern.confidence}`
                                    : `${prettyPattern(pattern.pattern_type)} — log in to see sentiment & confidence`}
                                  style={{
                                    display: "block", fontSize: 10, lineHeight: 1.3,
                                    marginTop: 3,
                                    color: pattern.sentiment ? sentimentColor(pattern.sentiment) : "var(--bs-secondary)",
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                  }}
                                >
                                  ◆ {prettyPattern(pattern.pattern_type)}
                                </span>
                              )}
                              {momNotable && (
                                <span
                                  title={`Momentum rank ${mom!.upr_rank.toFixed(2)}/100 (${mom!.upr_rank_chg >= 0 ? "▲" : "▼"}${Math.abs(mom!.upr_rank_chg).toFixed(2)} vs 2w ago)`}
                                  style={{ display: "block", fontSize: 10, lineHeight: 1.3, marginTop: 3, color: "var(--bs-secondary)" }}
                                >
                                  Mom {mom!.upr_rank.toFixed(2)} {mom!.upr_rank >= MOM_HIGH ? "▲" : "▼"}
                                </span>
                              )}
                            </td>
                            <td className="text-muted" title={e.name} style={{ verticalAlign: "top", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {e.name}
                            </td>
                            <td className="text-muted" style={{ verticalAlign: "top", fontSize: 12, whiteSpace: "nowrap" }}>
                              {f?.market_cap ? formatMarketCap(f.market_cap) : "—"}
                            </td>
                            <td className="text-end pe-3 text-muted" style={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                              {e.eps_forecast ?? "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <p className="text-muted mt-4" style={{ fontSize: 12 }}>
        Data sourced from Nasdaq earnings calendar. Dates and estimates may change.
        Signals are from the DejaChart pattern matcher — click any ticker to see the full
        match (conviction, analogs, and forecast). The badge marks that a signal exists; a
        ⚠ flags that imminent earnings is an event risk the analog forecast doesn't price.
      </p>
    </Container>
  );
}
